<script lang="ts">
  import { track } from '../lib/analytics';
  import { loadRecord, winRate } from '../lib/record';
  interface Props {
    onstart: () => void;
    onversus: () => void;
    onlegal: () => void;
    ontoast: (message: string) => void;
  }
  let { onstart, onversus, onlegal, ontoast }: Props = $props();

  /**
   * Read once on mount. The strip is hidden until a match has actually been
   * played -- "0 played, 0% won" is a worse first impression than no strip at
   * all, and the design never contemplated the empty state.
   */
  const record = loadRecord();

  /**
   * Which mode was tapped, including the two that only show a "coming soon"
   * toast. Demand for the unbuilt modes is exactly what should decide whether
   * 1v1 or tournaments gets built next, and it costs nothing to record now.
   */
  function pick(mode: string, go: () => void): void {
    track('mode_selected', { mode });
    go();
  }
</script>

<section class="screen active" aria-label="Home">
  <div class="body">
    <div class="hero">
      <p class="wordmark">pick<b>the11</b></p>
      <p class="tagline">Build a side. Prove it.</p>
    </div>

    <!--
      Test is tappable, not disabled: a dead button reads as broken, a message
      reads as a roadmap.
    -->
    <div class="formats" role="tablist" aria-label="Format">
      <button class="on" role="tab" aria-selected="true">T20</button>
      <button role="tab" aria-selected="false" onclick={() => ontoast('Test cricket — coming soon!')}>
        Test
      </button>
    </div>

    <button class="mode" onclick={() => pick('cpu', onstart)}>
      <svg class="watermark" aria-hidden="true"><use href="#i-bat" /></svg>
      <span class="ic"><svg><use href="#i-bat" /></svg></span>
      <span>
        <span class="t">You vs CPU</span>
        <span class="s">Draft your side, then play a full match</span>
      </span>
      <span class="go">›</span>
    </button>

    <button class="mode" onclick={() => pick('1v1', onversus)}>
      <svg class="watermark" aria-hidden="true"><use href="#i-users" /></svg>
      <span class="ic"><svg><use href="#i-users" /></svg></span>
      <span>
        <span class="t">1 vs 1</span>
        <span class="s">Invite a friend, play in your own time</span>
      </span>
      <span class="go">›</span>
    </button>

    <button class="mode" onclick={() => pick('tournament', () => ontoast('Tournaments — coming soon!'))}>
      <svg class="watermark" aria-hidden="true"><use href="#i-trophy" /></svg>
      <span class="ic"><svg><use href="#i-trophy" /></svg></span>
      <span>
        <span class="t">Tournament</span>
        <span class="s">Groups and knockout</span>
      </span>
      <span class="go">›</span>
    </button>

    {#if record.played > 0}
      <div class="record">
        <p class="eyebrow">Your record</p>
        <div class="grid">
          <div class="cell"><b class="tabular">{record.played}</b><s>Played</s></div>
          <div class="cell"><b class="tabular">{winRate(record)}%</b><s>Won</s></div>
          <div class="cell"><b class="tabular">{record.best}</b><s>Best</s></div>
        </div>
      </div>
    {/if}
  </div>

  <div class="actions">
    <button class="btn-primary" onclick={() => pick('cpu', onstart)}>Start drafting</button>
  </div>
  <footer class="disclaim">
    Unofficial fan-made game. Not affiliated with any cricket board, team, competition or player.
    <!--
      The only route to the legal and privacy text, so it is a real button
      rather than styled text: it has to be reachable by keyboard and read as
      tappable at 12px.
    -->
    <button type="button" class="linklike" onclick={onlegal}>Privacy &amp; legal</button>
  </footer>
</section>
